/**
 * Version Manager - handles localStorage migration and version tracking.
 */

// Initialize logger for this module - with fallback for when logger.js hasn't loaded yet
const logger = (typeof window !== 'undefined' && window.Logger)
  ? window.Logger.create('version-manager')
  : {
      debug: () => {},
      info: (...args) => console.log('[version-manager]', ...args),
      warn: (...args) => console.warn('[version-manager]', ...args),
      error: (...args) => console.error('[version-manager]', ...args)
    };

// Verbose logs only when debug is enabled (quieter production console)
function verboseLog(...args) {
  if (typeof window !== 'undefined' && window.__USEDFINDR_DEBUG__) {
    logger.info(...args);
  }
}

// Application version - increment this on breaking changes
export const APP_VERSION = 'v2.0.0-vite';

// Keys to preserve across version updates (Phase 3: no usedfindr_token - token in memory only)
const PRESERVED_KEYS = [
  'usedfindr_user',
  'usedfindr_cookie_consent',
  'usedfindr_saved_items'
];

// Keys to clear on version changes (cached data that may be incompatible)
const CLEARABLE_KEYS = [
  'usedfindr_items',
  'usedfindr_submissions',
  'usedfindr_reports',
  'usedfindr_removed_items',
  'usedfindr_admin_events',
  'usedfindr_product_clicks',
  'usedfindr_votes',
  'usedfindr_finds',
  'usedfindr_admin',
  'usedfindr_users'
];

/**
 * Check current version and migrate localStorage if needed
 * This function should be called as early as possible in the app lifecycle
 */
// Phase 3: clear stored tokens and admin email so old sessions don't rely on them
const PHASE3_CLEAR_KEYS = ['usedfindr_token', 'usedfindr_refresh_token', 'recentAdminEmail', 'recentAdminLoginTime'];

export function checkAndMigrateVersion() {
  try {
    // Phase 3: always clear token/admin keys from localStorage on load
    PHASE3_CLEAR_KEYS.forEach(key => {
      try {
        if (localStorage.getItem(key) !== null) {
          localStorage.removeItem(key);
        }
      } catch (e) { /* ignore */ }
    });

    const savedVersion = localStorage.getItem('usedfindr_app_version');
    
    // If versions match, no migration needed
    if (savedVersion === APP_VERSION) {
      verboseLog(`[VersionManager] Version ${APP_VERSION} - No migration needed`);
      return { migrated: false, version: APP_VERSION };
    }

    verboseLog(`[VersionManager] Version mismatch detected`);
    verboseLog(`[VersionManager] Saved: ${savedVersion || 'none'}, Current: ${APP_VERSION}`);

    // Preserve important data
    const preservedData = {};
    PRESERVED_KEYS.forEach(key => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preservedData[key] = value;
      }
    });

    verboseLog(`[VersionManager] Preserved ${Object.keys(preservedData).length} items:`, Object.keys(preservedData));

    // Clear all clearable keys
    let clearedCount = 0;
    CLEARABLE_KEYS.forEach(key => {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        clearedCount++;
      }
    });

    verboseLog(`[VersionManager] Cleared ${clearedCount} cached data items`);
    
    // Optionally migrate user data if structure changed
    if (preservedData.usedfindr_user) {
      try {
        const userData = JSON.parse(preservedData.usedfindr_user);
        // Example migration: ensure all expected fields exist
        // Add any field transformations here as needed
        const migratedUser = {
          ...userData,
          // Add default values for new fields if needed
          // For example: profileVersion: '2.0.0',
        };
        localStorage.setItem('usedfindr_user', JSON.stringify(migratedUser));
        verboseLog('[VersionManager] User data migrated');
      } catch (error) {
        logger.error('[VersionManager] Error migrating user data:', error);
        // If migration fails, restore original
        localStorage.setItem('usedfindr_user', preservedData.usedfindr_user);
      }
    }
    
    // Set new version
    localStorage.setItem('usedfindr_app_version', APP_VERSION);
    
    verboseLog(`[VersionManager] Migration complete - now at version ${APP_VERSION}`);
    
    return {
      migrated: true,
      previousVersion: savedVersion || 'unknown',
      currentVersion: APP_VERSION,
      preservedItems: Object.keys(preservedData),
      clearedItems: clearedCount
    };
    
  } catch (error) {
    logger.error('[VersionManager] Error during version check/migration:', error);
    
    // On error, still try to set the version to prevent infinite loops
    try {
      localStorage.setItem('usedfindr_app_version', APP_VERSION);
    } catch (e) {
      logger.error('[VersionManager] Could not set app version:', e);
    }
    
    return {
      migrated: false,
      error: error.message,
      version: APP_VERSION
    };
  }
}

/**
 * Force clear all localStorage (useful for debugging)
 * Use with caution - logs user out and clears all preferences
 */
export function forceResetLocalStorage() {
  if (confirm('This will clear all local data and log you out. Continue?')) {
    localStorage.clear();
    localStorage.setItem('usedfindr_app_version', APP_VERSION);
    logger.info('[VersionManager] localStorage reset complete');
    window.location.reload();
  }
}

/**
 * Get current version information
 */
export function getVersionInfo() {
  return {
    currentVersion: APP_VERSION,
    savedVersion: localStorage.getItem('usedfindr_app_version'),
    storageKeys: Object.keys(localStorage),
    storageSize: new Blob(Object.values(localStorage)).size
  };
}

// Auto-run version check when module loads
// This ensures migration happens before other scripts run
verboseLog('[VersionManager] Module loaded, checking version...');
const migrationResult = checkAndMigrateVersion();

// Expose version info to window for debugging
if (typeof window !== 'undefined') {
  window.__USEDFINDR_VERSION__ = APP_VERSION;
  window.__USEDFINDR_MIGRATION_RESULT__ = migrationResult;
  
  // Expose debug functions for developers
  window.__USEDFINDR_DEBUG__ = {
    getVersionInfo,
    forceReset: forceResetLocalStorage,
    checkVersion: checkAndMigrateVersion
  };
}

